import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { CalendarIcon, Users, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";

import { apartmentInfo, STORAGE_KEY } from "../config/siteData";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import { useLanguage } from "../context/LanguageContext";

// EmailJS konfiguracija - javni ključevi
const EMAILJS_SERVICE_ID = "service_t4ehl58";
const EMAILJS_TEMPLATE_ID = "template_ik8nzzd";
const EMAILJS_PUBLIC_KEY = "5RRMCwoC_-zpQCXcs";

export const InquiryForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { t } = useLanguage();

  // Validation schema with translated messages
  const formSchema = z.object({
    checkIn: z.date({
      required_error: t.inquiry.checkIn.replace(' *', ''),
    }),
    checkOut: z.date({
      required_error: t.inquiry.checkOut.replace(' *', ''),
    }),
    guests: z.string().min(1, t.inquiry.selectGuests),
    fullName: z.string().min(2, t.inquiry.fullName.replace(' *', '')),
    email: z.string().email(t.inquiry.emailPlaceholder),
    message: z.string().optional(),
    consent: z.boolean().refine((val) => val === true, {
      message: t.inquiry.consent.replace(' *', ''),
    }),
  }).refine((data) => data.checkOut > data.checkIn, {
    message: "Invalid date range",
    path: ["checkOut"],
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      message: "",
      guests: "",
      consent: false,
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    const checkInFormatted = format(data.checkIn, "dd.MM.yyyy");
    const checkOutFormatted = format(data.checkOut, "dd.MM.yyyy");

    // Priprema podataka za EmailJS
    const emailParams = {
      from_name: data.fullName,
      user_email: data.email,
      check_in: checkInFormatted,
      check_out: checkOutFormatted,
      guests: data.guests,
      message: data.message || "No message",
      apartment_name: "Apartmani Adriatic i Maestral",
    };

    try {
      // Pošalji email preko EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        emailParams,
        EMAILJS_PUBLIC_KEY
      );

      // Spremi u localStorage kao backup
      const inquiry = {
        id: Date.now().toString(),
        ...data,
        checkIn: checkInFormatted,
        checkOut: checkOutFormatted,
        submittedAt: new Date().toISOString(),
      };
      const existingInquiries = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      existingInquiries.push(inquiry);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existingInquiries));

      // Log u konzolu
      console.log("=== NOVI UPIT ZA APARTMANE POSLAN NA EMAIL ===");
      console.log("Ime i prezime:", data.fullName);
      console.log("Email:", data.email);
      console.log("Datum dolaska:", checkInFormatted);
      console.log("Datum odlaska:", checkOutFormatted);
      console.log("Broj osoba:", data.guests);
      console.log("Poruka:", data.message || "(nije unesena)");
      console.log("================================");

      setIsSuccess(true);
      toast.success(t.inquiry.successTitle, {
        description: t.inquiry.successMessage,
      });

      // Reset forme nakon 3 sekunde
      setTimeout(() => {
        form.reset();
        setIsSuccess(false);
      }, 3000);

    } catch (error) {
      console.error("Greška pri slanju emaila:", error);
      toast.error(t.inquiry.errorTitle, {
        description: t.inquiry.errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="upit"
      className="py-16 md:py-24 bg-[#004E64]"
      data-testid="inquiry-section"
    >
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-sm font-medium tracking-wide uppercase text-[#D4A373] mb-3 block">
            {t.inquiry.subtitle}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold font-['Outfit'] tracking-tight text-white"
            data-testid="inquiry-title"
          >
            {t.inquiry.title}
          </h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto">
            {t.inquiry.description}
          </p>
        </div>

        {/* Form Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl">
            {isSuccess ? (
              <div className="text-center py-8" data-testid="success-message">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold font-['Outfit'] text-[#0F172A] mb-2">
                  {t.inquiry.successTitle}
                </h3>
                <p className="text-[#64748b]">
                  {t.inquiry.successMessage}
                </p>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Date Fields Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Check-in Date */}
                    <FormField
                      control={form.control}
                      name="checkIn"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[#0F172A] font-medium">
                            {t.inquiry.checkIn}
                          </FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className={`w-full justify-start text-left font-normal ${
                                    !field.value && "text-muted-foreground"
                                  }`}
                                  data-testid="checkin-button"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {field.value ? (
                                    format(field.value, "dd.MM.yyyy")
                                  ) : (
                                    <span>{t.inquiry.selectDate}</span>
                                  )}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) => date < new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Check-out Date */}
                    <FormField
                      control={form.control}
                      name="checkOut"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[#0F172A] font-medium">
                            {t.inquiry.checkOut}
                          </FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className={`w-full justify-start text-left font-normal ${
                                    !field.value && "text-muted-foreground"
                                  }`}
                                  data-testid="checkout-button"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {field.value ? (
                                    format(field.value, "dd.MM.yyyy")
                                  ) : (
                                    <span>{t.inquiry.selectDate}</span>
                                  )}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) =>
                                  date < (form.getValues("checkIn") || new Date())
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Guests Select */}
                  <FormField
                    control={form.control}
                    name="guests"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#0F172A] font-medium">
                          {t.inquiry.guests}
                        </FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="guests-select">
                              <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                              <SelectValue placeholder={t.inquiry.selectGuests} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {[...Array(apartmentInfo.maxGuests)].map((_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>
                                {i + 1} {i === 0 ? t.inquiry.person : t.inquiry.persons}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Name & Email Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Full Name */}
                    <FormField
                      control={form.control}
                      name="fullName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[#0F172A] font-medium">
                            {t.inquiry.fullName}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t.inquiry.fullNamePlaceholder}
                              {...field}
                              data-testid="fullname-input"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Email */}
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[#0F172A] font-medium">
                            {t.inquiry.emailLabel}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder={t.inquiry.emailPlaceholder}
                              {...field}
                              data-testid="email-input"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Message */}
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[#0F172A] font-medium">
                          {t.inquiry.message}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t.inquiry.messagePlaceholder}
                            rows={4}
                            {...field}
                            data-testid="message-textarea"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Consent Checkbox */}
                  <FormField
                    control={form.control}
                    name="consent"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="consent-checkbox"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm text-[#64748b] font-normal cursor-pointer">
                            {t.inquiry.consent}
                          </FormLabel>
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary py-6 text-lg rounded-xl font-semibold"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 spinner" />
                        {t.inquiry.sending}
                      </>
                    ) : (
                      t.inquiry.submit
                    )}
                  </Button>
                </form>
              </Form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
